/**
 * Microphone recorder that hands back the complete container file at stop.
 *
 * One `MediaRecorder` for the storage leg only — the live-transcript leg taps
 * the same `stream` through `pcm16-capture.ts`, so nothing here needs to be
 * chunked or replayed. The open stream is exposed for that tap and for a
 * waveform/level meter.
 */

/** Preference order; the first the browser supports wins (Safari: audio/mp4). */
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const EXTENSION_BY_CONTAINER: Record<string, string> = {
  "audio/webm": "webm",
  "audio/mp4": "m4a",
  "audio/ogg": "ogg",
};

export interface AudioRecording {
  file: File;
  durationMs: number;
}

export interface AudioRecorderHandle {
  readonly mimeType: string;
  /** The open microphone stream — for the PCM tap and a waveform; never stop its tracks. */
  readonly stream: MediaStream;
  /** Stops capture, releases the mic, and returns the complete recording. Idempotent. */
  stop: () => Promise<AudioRecording>;
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

/** `audio/webm;codecs=opus` → `webm`; unknown containers fall back to `webm`. */
export function fileExtensionForMime(mimeType: string): string {
  const container = mimeType.split(";")[0].trim().toLowerCase();
  return EXTENSION_BY_CONTAINER[container] ?? "webm";
}

export async function startAudioRecorder(): Promise<AudioRecorderHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = pickMimeType();
  const recorder = new MediaRecorder(
    stream,
    mimeType ? { mimeType } : undefined,
  );
  const chunks: Blob[] = [];
  const startedAt = Date.now();

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  recorder.start();

  const effectiveMime = recorder.mimeType || mimeType || "audio/webm";
  // Idempotent: a second stop() (teardown during a stop already in flight)
  // must join the first, not re-arm `onstop` and strand the first promise.
  let stopping: Promise<AudioRecording> | null = null;

  return {
    mimeType: effectiveMime,
    stream,
    stop() {
      if (stopping) return stopping;
      stopping = new Promise<AudioRecording>((resolve) => {
        recorder.onstop = () => {
          for (const track of stream.getTracks()) track.stop();
          const blob = new Blob(chunks, { type: effectiveMime });
          const ext = fileExtensionForMime(effectiveMime);
          resolve({
            file: new File([blob], `recording-${startedAt}.${ext}`, {
              type: effectiveMime,
            }),
            durationMs: Date.now() - startedAt,
          });
        };
        if (recorder.state === "inactive") recorder.onstop(new Event("stop"));
        else recorder.stop();
      });
      return stopping;
    },
  };
}
